import {
    chartRefreshScheduler,
    computeStaleState,
    formatAge,
    FrameBudgetTracker,
    measureFrame,
    RingBuffer,
    getStaleThresholdMs,
    setSnapshot,
    getSnapshot,
    reportChartRenderMetric,
} from '../../charts/engine';
import {
    renderDonutChart,
    renderAreaChart,
    renderLineChart,
    renderHeatmapGrid,
    renderTreemapChart,
    renderWaterfallChart,
    renderCorrelationMatrix,
    type TreemapItem,
    type WaterfallStep,
} from '../../charts/primitives';
import * as zerodha from '../../broker/zerodha';
import * as groww from '../../broker/groww';
import * as upstox from '../../broker/upstox';
import { isFeatureEnabled } from '../../services/runtime-config';

type UnifiedHolding = {
    symbol: string;
    broker: string;
    quantity: number;
    investedValue: number;
    currentValue: number;
    pnl: number;
};

type PortfolioSnapshot = {
    holdings: UnifiedHolding[];
    pnlSeries: number[];
    lastUpdatedAt: number;
};

const teardownMap = new WeakMap<HTMLElement, () => void>();

function formatINR(value: number): string {
    return `₹${Math.abs(value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function hashToUnit(seed: string): number {
    let h = 2166136261;
    for (let i = 0; i < seed.length; i += 1) {
        h ^= seed.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return (h >>> 0) / 4294967295;
}

function percentile(sorted: number[], p: number): number {
    if (sorted.length === 0) return 0;
    const idx = Math.max(0, Math.min(sorted.length - 1, Math.floor(p * (sorted.length - 1))));
    return sorted[idx];
}

async function fetchUnifiedHoldings(): Promise<UnifiedHolding[]> {
    const output: UnifiedHolding[] = [];

    const [zResult, gResult, uResult] = await Promise.allSettled([
        zerodha.getHoldings(),
        groww.getStockHoldings(),
        upstox.getHoldings(),
    ]);

    if (zResult.status === 'fulfilled') {
        zResult.value.forEach(h => {
            output.push({
                symbol: h.symbol,
                broker: 'Zerodha',
                quantity: h.quantity,
                investedValue: h.investedValue,
                currentValue: h.currentValue,
                pnl: h.pnl,
            });
        });
    }

    if (gResult.status === 'fulfilled') {
        gResult.value.forEach(h => {
            output.push({
                symbol: h.symbol,
                broker: 'Groww',
                quantity: h.quantity,
                investedValue: h.investedValue,
                currentValue: h.currentValue,
                pnl: h.pnl,
            });
        });
    }

    if (uResult.status === 'fulfilled') {
        uResult.value.forEach(h => {
            output.push({
                symbol: h.symbol,
                broker: 'Upstox',
                quantity: h.quantity,
                investedValue: h.investedValue,
                currentValue: h.currentValue,
                pnl: h.currentValue - h.investedValue,
            });
        });
    }

    return output;
}

export function renderPhase2PortfolioEngine(container: HTMLElement): void {
    if (!isFeatureEnabled('charts_v2_engine')) {
        container.innerHTML = '<div class="wm-empty">charts_v2_engine flag is disabled.</div>';
        return;
    }

    teardownMap.get(container)?.();

    container.innerHTML = `
      <div class="v2-chart-card glass-card">
        <div class="card-header">
          <h3>Phase 2 Engine: Portfolio Pulse</h3>
          <span class="text-xs text-muted">Tier C refresh</span>
        </div>

        <div id="v2-portfolio-summary" class="terminal-stat-strip" style="grid-template-columns: repeat(4, minmax(0, 1fr)); margin: 0 0 var(--space-3) 0;"></div>

        <div class="v2-chart-grid">
          <div class="v2-chart-slot">
            <div class="v2-chart-title">Broker Allocation</div>
            <canvas id="v2-portfolio-donut" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Portfolio P&L Timeline</div>
            <canvas id="v2-portfolio-line" class="v2-chart-canvas"></canvas>
          </div>

                    <div class="v2-chart-slot">
                        <div class="v2-chart-title">PnL Waterfall by Position</div>
                        <canvas id="v2-portfolio-waterfall" class="v2-chart-canvas"></canvas>
                    </div>

                    <div class="v2-chart-slot">
                        <div class="v2-chart-title">Position Correlation Matrix</div>
                        <canvas id="v2-portfolio-corr" class="v2-chart-canvas"></canvas>
                    </div>

                    <div class="v2-chart-slot">
                        <div class="v2-chart-title">VaR Stress Distribution</div>
                        <canvas id="v2-portfolio-var" class="v2-chart-canvas"></canvas>
                    </div>

                    <div class="v2-chart-slot">
                        <div class="v2-chart-title">VaR Snapshot (95%)</div>
                        <div id="v2-portfolio-var-meta" class="v2-liquidity-warnings"></div>
                    </div>

                    <div class="v2-chart-slot">
                        <div class="v2-chart-title">Risk Concentration Grid</div>
                        <canvas id="v2-portfolio-risk-grid" class="v2-chart-canvas"></canvas>
                    </div>

                    <div class="v2-chart-slot">
                        <div class="v2-chart-title">Exposure Treemap</div>
                        <div id="v2-portfolio-treemap"></div>
                    </div>

                    <div class="v2-chart-slot">
                        <div class="v2-chart-title">Broker Concentration Meter</div>
                        <div id="v2-portfolio-broker-concentration" class="v2-sector-board"></div>
                    </div>

          <div class="v2-chart-slot v2-chart-slot--wide">
            <div class="v2-chart-title">Top Positions by Absolute P&L</div>
            <div id="v2-portfolio-board" class="v2-sector-board"></div>
          </div>
        </div>

        <div class="v2-chart-meta">
          <span id="v2-portfolio-freshness" class="text-xs">Waiting for broker portfolio feed...</span>
          <span id="v2-portfolio-frame" class="text-xs text-muted" style="margin-left: var(--space-3);">Frame: --</span>
        </div>

        <div id="v2-portfolio-stale" class="v2-stale-overlay" style="display:none;">
          Portfolio feed delayed. Rendering cached snapshot.
        </div>
      </div>
    `;

    const summaryNode = container.querySelector('#v2-portfolio-summary') as HTMLElement | null;
    const donutCanvas = container.querySelector('#v2-portfolio-donut') as HTMLCanvasElement | null;
    const lineCanvas = container.querySelector('#v2-portfolio-line') as HTMLCanvasElement | null;
    const waterfallCanvas = container.querySelector('#v2-portfolio-waterfall') as HTMLCanvasElement | null;
    const corrCanvas = container.querySelector('#v2-portfolio-corr') as HTMLCanvasElement | null;
    const varCanvas = container.querySelector('#v2-portfolio-var') as HTMLCanvasElement | null;
    const varMetaNode = container.querySelector('#v2-portfolio-var-meta') as HTMLElement | null;
    const riskGridCanvas = container.querySelector('#v2-portfolio-risk-grid') as HTMLCanvasElement | null;
    const treemapNode = container.querySelector('#v2-portfolio-treemap') as HTMLElement | null;
    const brokerConcentrationNode = container.querySelector('#v2-portfolio-broker-concentration') as HTMLElement | null;
    const boardNode = container.querySelector('#v2-portfolio-board') as HTMLElement | null;
    const freshnessNode = container.querySelector('#v2-portfolio-freshness') as HTMLElement | null;
    const frameNode = container.querySelector('#v2-portfolio-frame') as HTMLElement | null;
    const staleNode = container.querySelector('#v2-portfolio-stale') as HTMLElement | null;

    if (!summaryNode || !donutCanvas || !lineCanvas || !waterfallCanvas || !corrCanvas || !varCanvas || !varMetaNode || !riskGridCanvas || !treemapNode || !brokerConcentrationNode || !boardNode || !freshnessNode || !frameNode || !staleNode) return;

    const snapshotKey = 'phase2:portfolio-engine';
    const staleThresholdMs = getStaleThresholdMs('C');
    const frameTracker = new FrameBudgetTracker(200, 16);
    const pnlSeries = new RingBuffer<number>(180);

    let lastUpdatedAt: number | null = null;
    let lastHoldings: UnifiedHolding[] = [];

    const draw = (holdings: UnifiedHolding[]) => {
        const totalInvested = holdings.reduce((sum, h) => sum + h.investedValue, 0);
        const totalCurrent = holdings.reduce((sum, h) => sum + h.currentValue, 0);
        const totalPnl = totalCurrent - totalInvested;

        const sample = measureFrame('portfolio-engine', () => {
            const pnlTone = totalPnl >= 0 ? 'terminal-text--up' : 'terminal-text--down';
            summaryNode.innerHTML = `
              <div class="terminal-stat"><span class="terminal-stat__label">Total Invested</span><span class="terminal-stat__value">${formatINR(totalInvested)}</span></div>
              <div class="terminal-stat"><span class="terminal-stat__label">Current Value</span><span class="terminal-stat__value">${formatINR(totalCurrent)}</span></div>
              <div class="terminal-stat"><span class="terminal-stat__label">Total P&L</span><span class="terminal-stat__value ${pnlTone}">${totalPnl >= 0 ? '+' : '-'}${formatINR(totalPnl)}</span></div>
              <div class="terminal-stat"><span class="terminal-stat__label">Positions</span><span class="terminal-stat__value">${holdings.length}</span></div>
            `;

            const brokerTotals = new Map<string, number>();
            holdings.forEach(h => {
                brokerTotals.set(h.broker, (brokerTotals.get(h.broker) || 0) + h.currentValue);
            });

            const values = Array.from(brokerTotals.values());
            const palette = ['#5aa6ff', '#15b57a', '#ff9f43', '#d94736'];
            renderDonutChart(donutCanvas, values, palette);

                        const totalBrokerValue = Math.max(1, values.reduce((sum, value) => sum + value, 0));
                        const topBrokerShare = values.length > 0 ? (Math.max(...values) / totalBrokerValue) * 100 : 0;
                        const hhi = values.reduce((sum, value) => {
                                const share = value / totalBrokerValue;
                                return sum + (share * share);
                        }, 0) * 10000;
                        const concentrationTone = hhi >= 3000 ? 'terminal-text--down' : hhi >= 1800 ? '' : 'terminal-text--up';
                        brokerConcentrationNode.innerHTML = `
                            <div class="terminal-list__row"><span>Top Broker Share</span><strong>${topBrokerShare.toFixed(1)}%</strong></div>
                            <div class="terminal-list__row"><span>HHI Concentration</span><strong class="${concentrationTone}">${hhi.toFixed(0)}</strong></div>
                        `;

            renderLineChart(lineCanvas, pnlSeries.toArray(), '#5aa6ff');

            const waterfallSteps: WaterfallStep[] = holdings
                .slice()
                .sort((a, b) => Math.abs(b.pnl) - Math.abs(a.pnl))
                .slice(0, 6)
                .map((holding) => ({
                    label: holding.symbol,
                    value: holding.pnl,
                }));
            const others = holdings.slice(6).reduce((sum, item) => sum + item.pnl, 0);
            if (Math.abs(others) > 0) {
                waterfallSteps.push({ label: 'Others', value: others });
            }
            renderWaterfallChart(waterfallCanvas, waterfallSteps);

            const corrUniverse = holdings
                .slice()
                .sort((a, b) => b.currentValue - a.currentValue)
                .slice(0, 6);
            const corrLabels = corrUniverse.map((h) => h.symbol);
            const corrMatrix = corrUniverse.map((rowHolding, rowIdx) => {
                return corrUniverse.map((colHolding, colIdx) => {
                    if (rowIdx === colIdx) return 1;

                    const base = hashToUnit(`${rowHolding.symbol}:${colHolding.symbol}`);
                    const signSeed = hashToUnit(`${colHolding.symbol}:${rowHolding.symbol}`);
                    const rowPnlPct = rowHolding.currentValue > 0 ? rowHolding.pnl / rowHolding.currentValue : 0;
                    const colPnlPct = colHolding.currentValue > 0 ? colHolding.pnl / colHolding.currentValue : 0;
                    const pnlAffinity = Math.max(-1, Math.min(1, (rowPnlPct * colPnlPct) * 30));
                    const stochastic = (base * 1.4) - 0.7;
                    const signAdjust = signSeed > 0.5 ? 1 : -1;
                    return Math.max(-1, Math.min(1, (pnlAffinity * 0.55) + (stochastic * 0.45 * signAdjust)));
                });
            });
            renderCorrelationMatrix(corrCanvas, corrLabels, corrMatrix);

                        const scenarioCount = 160;
                        const scenarioReturns = Array.from({ length: scenarioCount }, (_, idx) => {
                                const scenarioShock = (idx / Math.max(1, scenarioCount - 1)) * 2 - 1;
                                const aggregate = holdings.reduce((sum, holding) => {
                                        const weight = totalCurrent > 0 ? holding.currentValue / totalCurrent : 0;
                                        const pnlPct = holding.currentValue > 0 ? (holding.pnl / holding.currentValue) : 0;
                                        const symbolNoise = (hashToUnit(`${holding.symbol}:${idx}`) * 2) - 1;
                                        const sensitivity = 0.45 + (hashToUnit(`${holding.symbol}:beta`) * 0.75);
                                        const stressed = (scenarioShock * sensitivity * 0.05) + (symbolNoise * 0.01) + (pnlPct * 0.15);
                                        return sum + (weight * stressed);
                                }, 0);
                                return aggregate;
                        }).sort((a, b) => a - b);

                        renderAreaChart(varCanvas, scenarioReturns, '#ff9f43', 'rgba(255,159,67,0.20)');

                        const var95Return = percentile(scenarioReturns, 0.05);
                        const var99Return = percentile(scenarioReturns, 0.01);
                        const tail = scenarioReturns.filter((value) => value <= var95Return);
                        const cvar95Return = tail.length > 0 ? tail.reduce((sum, value) => sum + value, 0) / tail.length : var95Return;

                        const var95 = Math.max(0, -(var95Return * totalCurrent));
                        const var99 = Math.max(0, -(var99Return * totalCurrent));
                        const cvar95 = Math.max(0, -(cvar95Return * totalCurrent));

                        varMetaNode.innerHTML = `
                            <span class="v2-liquidity-chip">VaR95 ${formatINR(var95)}</span>
                            <span class="v2-liquidity-chip">CVaR95 ${formatINR(cvar95)}</span>
                            <span class="v2-liquidity-chip">VaR99 ${formatINR(var99)}</span>
                            <span class="v2-liquidity-chip">Scenarios ${scenarioCount}</span>
                        `;

            const totalCurrentValue = Math.max(1, totalCurrent);
            const riskCells = holdings.slice(0, 12).map((holding, idx) => {
                const concentration = (holding.currentValue / totalCurrentValue) * 100;
                const pnlPct = holding.currentValue > 0
                    ? ((holding.pnl / holding.currentValue) * 100)
                    : 0;
                const riskScore = Math.max(-5, Math.min(5, (concentration * 0.12) + pnlPct));
                return {
                    x: idx % 6,
                    y: Math.floor(idx / 6),
                    value: riskScore,
                };
            });
            renderHeatmapGrid(riskGridCanvas, riskCells, 6, 2);

            const treemapData: TreemapItem[] = holdings
                .slice()
                .sort((a, b) => b.currentValue - a.currentValue)
                .slice(0, 10)
                .map((holding) => ({
                    label: holding.symbol,
                    value: Math.max(1, holding.currentValue),
                    change: holding.currentValue > 0 ? (holding.pnl / holding.currentValue) * 100 : 0,
                }));
            renderTreemapChart(treemapNode, treemapData);

            const top = [...holdings]
                .sort((a, b) => Math.abs(b.pnl) - Math.abs(a.pnl))
                .slice(0, 10);

            if (top.length === 0) {
                boardNode.innerHTML = '<div class="wm-empty">No live holdings available.</div>';
            } else {
                boardNode.innerHTML = top.map(item => {
                    const tone = item.pnl >= 0 ? 'terminal-text--up' : 'terminal-text--down';
                    const sign = item.pnl >= 0 ? '+' : '-';
                    return `<div class="terminal-list__row"><span>${item.symbol} <span class="text-xs text-muted">(${item.broker})</span></span><strong class="${tone}">${sign}${formatINR(item.pnl)}</strong></div>`;
                }).join('');
            }
        });

        frameTracker.push(sample.label, sample.durationMs);
        const avg = frameTracker.getAverageMs();
        const within = frameTracker.isWithinBudget(sample.durationMs);
        frameNode.textContent = `Frame: ${sample.durationMs.toFixed(2)}ms | avg ${avg.toFixed(2)}ms | budget ${within ? 'OK' : 'OVER'}`;

        reportChartRenderMetric({
            id: 'phase2-portfolio',
            label: 'Portfolio Pulse',
            latestMs: sample.durationMs,
            averageMs: avg,
            budgetMs: frameTracker.getBudgetMs(),
            withinBudget: within,
        });

        const stale = computeStaleState(lastUpdatedAt, staleThresholdMs);
        staleNode.style.display = stale.stale ? 'flex' : 'none';
        freshnessNode.textContent = stale.stale
            ? `Stale: ${formatAge(stale.ageMs)} old`
            : `Live update ${new Date(lastUpdatedAt || Date.now()).toLocaleTimeString('en-IN')}`;
    };

    const persist = () => {
        setSnapshot<PortfolioSnapshot>(snapshotKey, {
            holdings: lastHoldings,
            pnlSeries: pnlSeries.toArray(),
            lastUpdatedAt: lastUpdatedAt || Date.now(),
        });
    };

    const update = async () => {
        try {
            const holdings = await fetchUnifiedHoldings();
            const totalInvested = holdings.reduce((sum, h) => sum + h.investedValue, 0);
            const totalCurrent = holdings.reduce((sum, h) => sum + h.currentValue, 0);
            const totalPnl = totalCurrent - totalInvested;

            pnlSeries.push(totalPnl);
            lastHoldings = holdings;
            lastUpdatedAt = Date.now();
            persist();
            draw(holdings);
        } catch {
            if (lastHoldings.length > 0) draw(lastHoldings);
        }
    };

    const persisted = getSnapshot<PortfolioSnapshot>(snapshotKey);
    if (persisted?.holdings) {
        lastHoldings = persisted.holdings;
        persisted.pnlSeries.forEach(value => pnlSeries.push(value));
        lastUpdatedAt = persisted.lastUpdatedAt;
        draw(lastHoldings);
    }

    const taskId = `phase2-portfolio-${Math.random().toString(36).slice(2)}`;
    const unregister = chartRefreshScheduler.register(taskId, 'C', update, 15000);

    teardownMap.set(container, () => {
        unregister();
    });
}
